import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AngularFireAuth } from "@angular/fire/auth";
import { AngularFirestore } from "@angular/fire/firestore";
import { Observable, of, Subscription, BehaviorSubject, interval } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { UsersService } from "./users.service";
import { User } from "firebase";
import { DEFAULT_REDIRECT_PAGE } from "../../assets/constant";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly DEFAULT_SESSION_DURATION_IN_MIN = 4 * 60 * 60 * 1000;
  private readonly SESSION_KEY = "firebase_session_timestamp";
  private readonly SESSION_SIGNATURE_KEY = "firebase_session_signature";
  private readonly DATA_SIGNATURE_KEY = "firebase_data_signature";
  protected readonly USER_TYPE_ADMIN = "Admin";
  protected readonly ORGANISATION_ARR = ["Baas-Test", "Baas"];
  protected readonly CUSTOMER_ID = "P0obn63uWrgtETH6yZmMtvOm9At1";
  private readonly SECRET_KEY = "BatteryPool_Session_Secret@2025";

  private readonly PROTECTED_KEYS = [
    "firebase_session_timestamp",
    "firebase_session_signature",
    "currentUser",
    "currentUserType",
    "currentOrganisation",
    "environment",
    "latitude",
    "longitude",
    "userName",
  ];

  SESSION_DURATION_IN_MIN: number = this.DEFAULT_SESSION_DURATION_IN_MIN;
  user: User;
  usersServiceSubscription: Subscription;
  customerId: string;
  user$ = this.afAuth.authState;

  private sessionExpired$ = new BehaviorSubject<boolean>(false);
  private userSignedOut$ = new BehaviorSubject<boolean>(false);

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private usersServiceLocal: UsersService
  ) {
    this.initializeSessionMonitoring();
  }

  private initializeSessionMonitoring() {
    interval(10000).subscribe(() => {
      this.checkSessionValidity();
    });

    this.user$.subscribe((user) => {
      if (user) {
        this.validateExistingSession();
      } else {
        this.clearSession();
      }
    });
  }

  private generateDataSignature(): string {
    let dataString = "";
    this.PROTECTED_KEYS.forEach((key) => {
      const value = localStorage.getItem(key) || "";
      dataString += `${key}:${value}|`;
    });
    dataString += this.SECRET_KEY;

    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
      const char = dataString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private verifyDataIntegrity(): boolean {
    const storedSignature = localStorage.getItem(this.DATA_SIGNATURE_KEY);
    if (!storedSignature) return false;

    const currentSignature = this.generateDataSignature();
    const isValid = currentSignature === storedSignature;
    if (!isValid) this.logTamperingDetails();
    return isValid;
  }

  private logTamperingDetails() {
    this.PROTECTED_KEYS.forEach((key) => {
      localStorage.getItem(key);
    });
  }

  private updateDataSignature() {
    const signature = this.generateDataSignature();
    localStorage.setItem(this.DATA_SIGNATURE_KEY, signature);
  }

  private setSecureItem(key: string, value: string) {
    localStorage.setItem(key, value);
    this.updateDataSignature();
  }

  private generateSignature(timestamp: number): string {
    const data = timestamp + this.SECRET_KEY;
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private verifySignature(timestamp: number, signature: string): boolean {
    const expectedSignature = this.generateSignature(timestamp);
    return expectedSignature === signature;
  }

  private setSessionTimestamp() {
    const timestamp = Date.now();
    const signature = this.generateSignature(timestamp);
    this.setSecureItem(this.SESSION_KEY, timestamp.toString());
    this.setSecureItem(this.SESSION_SIGNATURE_KEY, signature);
  }

  private getSessionTimestamp(): number | null {
    if (!this.verifyDataIntegrity()) {
      this.handleDataTampering();
      return null;
    }

    const timestampStr = localStorage.getItem(this.SESSION_KEY);
    const signature = localStorage.getItem(this.SESSION_SIGNATURE_KEY);

    if (!timestampStr || !signature) return null;

    const timestamp = parseInt(timestampStr, 10);
    if (isNaN(timestamp)) {
      this.handleDataTampering();
      return null;
    }

    if (!this.verifySignature(timestamp, signature)) {
      this.handleDataTampering();
      return null;
    }

    const currentTime = Date.now();
    const maxAllowedTime = currentTime + 60000;
    if (timestamp > maxAllowedTime) {
      this.handleDataTampering();
      return null;
    }

    return timestamp;
  }

  private handleSessionTampering() {
    this.handleDataTampering();
  }

  private handleDataTampering() {
    this.logTamperingDetails();
    this.sessionExpired$.next(true);
    this.signOut(true);
  }

  public clearSession() {
    this.PROTECTED_KEYS.forEach((key) => {
      localStorage.removeItem(key);
    });

    localStorage.removeItem(this.DATA_SIGNATURE_KEY);
    localStorage.clear();

    // this.sessionExpired$.next(false);
    // this.userSignedOut$.next(false);
  }

  private isSessionValid(): boolean {
    if (!this.verifyDataIntegrity()) return false;

    const sessionTimestamp = this.getSessionTimestamp();
    if (!sessionTimestamp) return false;

    const currentTime = Date.now();
    const sessionAge = currentTime - sessionTimestamp;

    if (sessionAge < 0) {
      this.handleDataTampering();
      return false;
    }

    return sessionAge < this.SESSION_DURATION_IN_MIN;
  }

  getRemainingSessionTime(): number {
    const sessionTimestamp = this.getSessionTimestamp();
    if (!sessionTimestamp) return 0;

    const currentTime = Date.now();
    const sessionAge = currentTime - sessionTimestamp;
    const remaining = this.SESSION_DURATION_IN_MIN - sessionAge;

    return Math.max(0, remaining);
  }

  private checkSessionValidity() {
    if (!this.afAuth.auth.currentUser) return;
    const remainingTime = this.getRemainingSessionTime();
    if (remainingTime <= 0) {
      this.handleSessionExpiration();
    }
  }

  private validateExistingSession() {
    if (!this.isSessionValid()) {
      this.handleSessionExpiration();
    }
  }

  private async handleSessionExpiration() {
    this.sessionExpired$.next(true);
    await this.signOut(true);
  }

  async signIn(email: string, password: string) {
    this.setSecureItem("environment", "");

    try {
      const result = await this.afAuth.auth.signInWithEmailAndPassword(
        email,
        password
      );

      this.setSessionTimestamp();
      this.sessionExpired$.next(false);
      this.setSecureItem("currentUser", this.afAuth.auth.currentUser.uid);

      let uid = this.afAuth.auth.currentUser.uid;

      try {
        const uidDoc = await this.afs
          .collection(environment.env.CustomersCollection)
          .doc(uid)
          .get()
          .toPromise();
        console.log("uidDoc.exists: ", uidDoc.exists);
        if (!uidDoc.exists) {
          uid = this.CUSTOMER_ID;
        }
      } catch (error) {
        console.log("error: ", error);
        uid = this.CUSTOMER_ID;
      }

      console.log("uid: ", uid);
      this.usersServiceSubscription = this.usersServiceLocal
        .getLoginUserData(uid)
        .subscribe((res) => {
          if (res) {
            if (!res["isActive"]) {
              window.alert("Invalid User!");
              this.signOut();
              return;
            }
            if (
              res["UserType"] !== this.USER_TYPE_ADMIN ||
              !this.ORGANISATION_ARR.includes(res["Organisation"])
            ) {
              window.alert("Not a valid user type or organisation!");
              this.signOut();
              return;
            }

            if (
              !res["listOfAllowableEmails"] ||
              !res["listOfAllowableEmails"].includes(email)
            ) {
              if (res["Email"] !== email) {
                window.alert("Invalid User!");
                this.signOut();
                return;
              }
            }

            this.setSecureItem(
              "userName",
              `@${email.split("@")[0] || "Admin"}`
            );
            this.setSecureItem("currentUserType", res["UserType"]);
            this.setSecureItem("currentOrganisation", res["Organisation"]);

            if (res.hasOwnProperty("Location")) {
              this.setSecureItem("latitude", res["Location"].latitude);
              this.setSecureItem("longitude", res["Location"].longitude);
            } else {
              this.setSecureItem("latitude", "18.5204");
              this.setSecureItem("longitude", "73.8567");
            }

            this.router.navigate([DEFAULT_REDIRECT_PAGE]);
          }
        });

      return result;
    } catch (error) {
      window.alert(error.message);
      throw error;
    }
  }

  async signOut(isExpired = false) {
    try {
      await this.afAuth.auth.signOut();

      if (this.usersServiceSubscription) {
        this.usersServiceSubscription.unsubscribe();
      }

      if (!isExpired) {
        this.router.navigate(["/auth/login"]);
      }
      this.clearSession();
    } catch (error) {}
  }

  updateEnvironment(env: string) {
    if (!this.isSessionValid()) {
      this.handleSessionExpiration();
      return;
    }
    this.setSecureItem("environment", env);
  }

  fetchMenuItems(): Observable<any> {
    if (!this.isSessionValid()) {
      this.handleSessionExpiration();
      throw new Error("Session expired");
    }

    this.customerId = localStorage.getItem("currentUser");
    if (!this.customerId) {
      throw new Error("No customer ID found in localStorage");
    }

    return this.afs
      .doc(`Customers/${this.CUSTOMER_ID}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          const data = actions.payload.data() as any | undefined;
          if (data) {
            const id = actions.payload.id;
            this.updateEnvironment(data.env || "");
            this.SESSION_DURATION_IN_MIN = data["sessionDurationInMinutes"]
              ? data["sessionDurationInMinutes"] * 60 * 1000
              : this.DEFAULT_SESSION_DURATION_IN_MIN;
            return { id, ...data };
          } else {
            throw new Error("No data found for customer");
          }
        })
      );
  }

  loggedIn(): boolean {
    const hasUser = localStorage.getItem("currentUser") !== null;
    const hasValidSession = this.isSessionValid();
    if (hasUser && !hasValidSession) {
      this.handleSessionExpiration();
      return false;
    }
    return hasUser && hasValidSession;
  }

  isAuthenticated(): Observable<boolean> {
    return this.user$.pipe(map((user) => !!user && this.isSessionValid()));
  }

  getCurrentUserId(): string | null {
    if (!this.isSessionValid()) return null;
    return (
      this.afAuth.auth.currentUser.uid || localStorage.getItem("currentUser")
    );
  }

  getCurrentUser(): Observable<any> {
    return this.user$.pipe(
      switchMap((user) => {
        if (user && this.isSessionValid()) {
          return this.usersServiceLocal.getLoginUserData(user.uid);
        }
        return of(null);
      })
    );
  }

  getSessionExpired(): Observable<boolean> {
    return this.sessionExpired$.asObservable();
  }

  getUserSignedOut(): Observable<boolean> {
    return this.userSignedOut$.asObservable();
  }

  clearSessionFlags() {
    this.sessionExpired$.next(false);
    this.userSignedOut$.next(false);
  }

  formatRemainingTime(): string {
    const remaining = this.getRemainingSessionTime();
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  getCurrentUserType(): string | null {
    if (!this.isSessionValid()) return null;
    return localStorage.getItem("currentUserType");
  }

  getCurrentOrganisation(): string | null {
    if (!this.isSessionValid()) return null;
    return localStorage.getItem("currentOrganisation");
  }

  getCurrentLocation(): { latitude: string; longitude: string } | null {
    if (!this.isSessionValid()) return null;
    const latitude = localStorage.getItem("latitude");
    const longitude = localStorage.getItem("longitude");
    if (latitude && longitude) {
      return { latitude, longitude };
    }
    return null;
  }

  getCurrentEnvironment(): string | null {
    if (!this.isSessionValid()) return null;
    return localStorage.getItem("environment");
  }

  getCurrentUserName(): string | null {
    if (!this.isSessionValid()) return null;
    return localStorage.getItem("userName");
  }
}
